import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Bell, ArrowLeft, Save, Loader2 } from 'lucide-react';

export default function NotificationPreferences() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState({});
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/notifications/preferences').then(res => {
      setPrefs(res.prefs || {});
      setEvents(res.events || {});
      setLoading(false);
    }).catch(() => { toast.error('Failed to load preferences'); setLoading(false); });
  }, []);

  const toggle = (k) => setPrefs(p => ({ ...p, [k]: !p[k] }));
  const save = async () => {
    setSaving(true);
    try {
      await apiPut('/notifications/preferences', { prefs });
      toast.success('Preferences saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="p-6 space-y-6" data-testid="notification-prefs-page">
      <button onClick={() => navigate('/app/settings')} className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-display font-bold text-slate-500 hover:text-[#1F7A6E]">
        <ArrowLeft size={14} /> Back to Settings
      </button>

      <PageHeader title="Notification Preferences" subtitle="Choose which email notifications you want to receive. Changes apply immediately to all future events." icon={Bell} />

      {loading ? <div className="text-sm text-slate-500">Loading…</div> : (
        <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100 max-w-3xl">
          {Object.entries(events).map(([key, meta]) => (
            <label key={key} className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50">
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-sm text-[#0F172A]">{meta.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{meta.description}</div>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => toggle(key)}
                  type="button"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${prefs[key] ? 'bg-[#1F7A6E]' : 'bg-slate-300'}`}
                  data-testid={`pref-${key}`}
                  aria-pressed={prefs[key]}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${prefs[key] ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </label>
          ))}
        </div>
      )}

      <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50" data-testid="save-prefs">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {saving ? 'Saving…' : 'Save Preferences'}
      </button>
    </div>
  );
}
